/**
 * BANDEJA DE CONCILIACION POR DIA (BAN-BANDEJA-PROPUESTAS-T1, Ares #28116).
 *
 * Audiencia: el administrador en el computador, mostrandole el mes a Argemiro O; tambien se lee a 375 px.
 * Vive en Auditoria > Conciliacion banco > "Propuestas por dia". Por cada dia del mes elegido muestra lo que el
 * servidor propone (GET /api/v1/banco/propuestas): que movimientos del banco cruzan con una venta, cuales quedan
 * pendientes, cuales son de Nexo y que salidas no tienen gasto. Nada se marca sin la decision del administrador:
 * Aceptar / Rechazar / Revisar por dia, y por cada salida sin gasto el egreso que se crearia (con marca o sin ella).
 *
 * Los numeros son los del servidor; esta pantalla no calcula nada del banco.
 *
 * @module presentation/components/conciliacion/BandejaPropuestasBanco
 */

import { BancoPropuestasApiClient as ClienteReal, DECISIONES } from '../../../infrastructure/api/BancoPropuestasApiClient.js';
import { normalizarPropuestas, rangoDelMes, estadoDelDia, resumenBandeja, filtrarDias, movimientosDelDia, admiteDecision } from '../../../application/conciliacion/bandejaPropuestas.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../../shared/texto.js';
// Se reexporta con el mismo nombre para no romper a quien lo importa de este archivo.
export { esc };
export const plata = (n) => '$ ' + Math.round(Number(n) || 0).toLocaleString('es-CO');
const DIAS_SEMANA = ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** 'yyyy-MM-dd' -> 'mar 12-may' */
export function fechaCorta(f) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(f || ''));
  if (!m) return esc(f);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return `${DIAS_SEMANA[d.getUTCDay()]} ${Number(m[3])}-${MESES[Number(m[2]) - 1]}`;
}

const ETIQUETA = {
  aceptar: { txt: 'Aceptado', cls: 'ok' },
  rechazar: { txt: 'Rechazado', cls: 'no' },
  revisar: { txt: 'Para revisar', cls: 'rev' },
  cuadra: { txt: 'Cuadra', cls: 'cuadra' },
  pendiente: { txt: 'Con diferencias', cls: 'dif' }
};

const MOTIVO = {
  SIN_SESION: 'Tu sesion se cerro. Vuelve a entrar.',
  PROHIBIDO: 'Solo el administrador decide la conciliacion.',
  NO_PUBLICADO: 'La API todavia no tiene esta ruta publicada.',
  SIN_RED: 'No pudimos hablar con la API ahora. Intenta de nuevo.',
  DATOS: 'La API no acepto los datos',
  ERROR_API: 'La API respondio con error'
};
export const fraseDelMotivo = (r) => (MOTIVO[r?.motivo] || 'No se pudo') + (r?.detalle && ['DATOS', 'ERROR_API'].includes(r.motivo) ? ': ' + r.detalle : '');

const CSS = `
.ban-prop{display:flex;flex-direction:column;gap:12px;min-width:0}
.ban-prop *{box-sizing:border-box}
.ban-prop .bp-barra{display:flex;flex-wrap:wrap;gap:8px;align-items:flex-end}
.ban-prop .bp-barra label{display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e}
.ban-prop input[type=month],.ban-prop input[type=text],.ban-prop select{min-width:0;width:auto;padding:5px 8px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#e1e4e8;font-size:0.875rem}
.ban-prop .bp-filtros{display:flex;flex-wrap:wrap;gap:6px}
.ban-prop .bp-chip{border:1px solid #30363d;border-radius:999px;padding:3px 10px;font-size:0.875rem;background:transparent;color:#c9d1d9;cursor:pointer}
.ban-prop .bp-chip[aria-pressed=true]{border-color:#1f6feb;color:#79c0ff}
.ban-prop .bp-mes{background:#161b22;border:1px solid #30363d;border-radius:8px;padding:10px 12px;font-size:0.875rem;color:#c9d1d9}
.ban-prop .bp-cuentas{display:flex;flex-wrap:wrap;gap:12px;margin-top:6px;font-size:0.875rem;color:#8b949e}
.ban-prop .bp-cuentas b{color:#e1e4e8}
.ban-prop .bp-dia{background:#0d1117;border:1px solid #30363d;border-radius:8px;padding:10px 12px;min-width:0}
.ban-prop .bp-cab{display:flex;flex-wrap:wrap;gap:8px;align-items:center;justify-content:space-between;cursor:pointer}
.ban-prop .bp-fecha{font-weight:600;color:#e1e4e8}
.ban-prop .bp-estado{font-size:0.875rem;border-radius:999px;padding:2px 8px;border:1px solid}
.ban-prop .bp-estado.ok{color:#3fb950;border-color:#238636}
.ban-prop .bp-estado.no{color:#f85149;border-color:#da3633}
.ban-prop .bp-estado.rev,.ban-prop .bp-estado.dif{color:#d29922;border-color:#9e6a03}
.ban-prop .bp-estado.cuadra{color:#58a6ff;border-color:#1f6feb}
.ban-prop .bp-texto{font-size:0.875rem;color:#c9d1d9;margin:6px 0 0;overflow-wrap:anywhere}
.ban-prop .bp-grupo{margin-top:10px}
.ban-prop .bp-grupo h4{font-size:0.875rem;color:#8b949e;margin:0 0 4px;font-weight:600;text-transform:uppercase;letter-spacing:.03em}
.ban-prop .bp-fila{display:flex;flex-wrap:wrap;gap:4px 12px;justify-content:space-between;font-size:0.875rem;color:#c9d1d9;padding:3px 0;border-bottom:1px solid #21262d}
.ban-prop .bp-fila span{overflow-wrap:anywhere;min-width:0}
.ban-prop .bp-acciones{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px}
.ban-prop .bp-egreso{display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin:4px 0 8px}
.ban-prop .bp-aviso{font-size:0.875rem;color:#d29922}
.ban-prop .bp-error{font-size:0.875rem;color:#f85149}
.ban-prop .bp-hecho{font-size:0.875rem;color:#3fb950}
`;

export function ponerCss(doc) {
  if (!doc || doc.getElementById('ban-prop-css')) return;
  const s = doc.createElement('style');
  s.id = 'ban-prop-css';
  s.textContent = CSS;
  doc.head.appendChild(s);
}

function mesActual(hoy = new Date()) {
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * @param {object} o
 * @param {string} o.businessId
 * @param {boolean} o.puedeDecidir   solo el rol admin (el servidor exige [RequireAdmin])
 * @param {object} [o.cliente]       BancoPropuestasApiClient (se cambia en las pruebas)
 * @param {string} [o.mes]           'yyyy-MM'; por defecto el mes en curso
 */
export function crearBandejaPropuestas({ businessId, puedeDecidir, cliente = ClienteReal, mes } = {}) {
  const st = {
    mes: mes || mesActual(),
    filtro: 'todos',
    cargando: false,
    error: null,
    datos: null,
    decisiones: {},            // fecha -> decision tomada en esta sesion
    egresos: {},               // bankMovementId -> decision tomada en esta sesion
    abiertos: new Set(),       // fechas desplegadas
    confirmando: null,         // fecha cuyo "Aceptar" pide confirmacion
    mensajes: {},              // fecha -> {cls, txt}
    ocupado: null
  };
  let raiz = null;

  async function cargar() {
    const r = rangoDelMes(st.mes);
    if (!r) { st.error = 'Elige un mes.'; pintar(); return; }
    st.cargando = true; st.error = null; pintar();
    const res = await cliente.propuestas({ businessId, desde: r.desde, hasta: r.hasta });
    st.cargando = false;
    if (!res.ok) { st.datos = null; st.error = fraseDelMotivo(res); pintar(); return; }
    st.datos = normalizarPropuestas(res.data);
    pintar();
  }

  async function decidirDia(fecha, decision) {
    st.ocupado = fecha; st.confirmando = null; pintar();
    const res = await cliente.decidirDia({ businessId, fecha, decision });
    st.ocupado = null;
    if (!res.ok) { st.mensajes[fecha] = { cls: 'bp-error', txt: fraseDelMotivo(res) }; pintar(); return; }
    st.decisiones[fecha] = decision;
    const d = res.data || {};
    st.mensajes[fecha] = { cls: 'bp-hecho', txt: d.mensaje || d.Mensaje || 'Decision guardada.' };
    pintar();
  }

  async function decidirEgreso(fecha, bankMovementId, decision) {
    const caja = raiz?.querySelector(`[data-bp-egreso="${CSS_ESC(bankMovementId)}"]`);
    const marcaPrivada = caja?.querySelector('[data-bp-marca]')?.value || '';
    const concepto = (caja?.querySelector('[data-bp-concepto]')?.value || '').trim();
    st.ocupado = bankMovementId; pintar();
    const res = await cliente.decidirEgreso({ businessId, bankMovementId, decision, marcaPrivada, concepto });
    st.ocupado = null;
    if (!res.ok) { st.mensajes[bankMovementId] = { cls: 'bp-error', txt: fraseDelMotivo(res) }; pintar(); return; }
    st.egresos[bankMovementId] = decision;
    const d = res.data || {};
    st.mensajes[bankMovementId] = { cls: 'bp-hecho', txt: d.mensaje || d.Mensaje || 'Decision guardada.' };
    pintar();
  }

  function htmlMovs(titulo, movs) {
    if (!movs.length) return '';
    return `<div class="bp-grupo"><h4>${esc(titulo)} (${movs.length})</h4>${movs.map((m) =>
      `<div class="bp-fila"><span>${esc(m.descripcion || 'sin descripcion')}${m.negocio ? ' · ' + esc(m.negocio) : ''}</span><span>${plata(m.valor)}</span></div>`).join('')}</div>`;
  }

  function htmlSalidas(d) {
    if (!d.salidasSinEgreso.length) return '';
    return `<div class="bp-grupo"><h4>Salidas del banco sin gasto (${d.salidasSinEgreso.length})</h4>${d.salidasSinEgreso.map((m) => {
      const e = m.egresoPropuesto;
      const decidido = st.egresos[m.bankMovementId] || e?.decision || null;
      const msg = st.mensajes[m.bankMovementId];
      const ocupado = st.ocupado === m.bankMovementId;
      // El banco trae la salida con signo menos; bajo "Salidas" se lee el monto.
      return `<div class="bp-fila"><span>${esc(m.descripcion || 'sin descripcion')}</span><span>${plata(Math.abs(m.valor))}</span></div>
        ${e && puedeDecidir && admiteDecision(decidido) ? `<div class="bp-egreso" data-bp-egreso="${esc(m.bankMovementId)}">
          <input type="text" data-bp-concepto value="${esc(e.conceptoSugerido || m.descripcion || '')}" aria-label="Concepto del egreso" style="flex:1 1 160px">
          <select data-bp-marca aria-label="Marca del egreso"><option value="">Egreso normal</option>${(e.marcas.length ? e.marcas : ['gerencial', 'personal']).map((k) => `<option value="${esc(k)}">${esc(k === 'gerencial' ? 'Gerencial' : k === 'personal' ? 'Personal' : k)}</option>`).join('')}</select>
          <button type="button" class="btn btn-primary btn-sm" data-bp-eg-dec="aceptar" data-bp-mov="${esc(m.bankMovementId)}" ${ocupado ? 'disabled' : ''}>Crear egreso</button>
          <button type="button" class="btn btn-ghost btn-sm" data-bp-eg-dec="rechazar" data-bp-mov="${esc(m.bankMovementId)}" ${ocupado ? 'disabled' : ''}>No crear</button>
          <button type="button" class="btn btn-ghost btn-sm" data-bp-eg-dec="revisar" data-bp-mov="${esc(m.bankMovementId)}" ${ocupado ? 'disabled' : ''}>Revisar</button>
        </div>` : ''}
        ${decidido ? `<div class="bp-hecho">Egreso: ${esc(ETIQUETA[decidido]?.txt || decidido)}</div>` : ''}
        ${msg ? `<div class="${msg.cls}">${esc(msg.txt)}</div>` : ''}`;
    }).join('')}</div>`;
  }

  function htmlDia(d) {
    const estado = estadoDelDia(d, st.decisiones);
    const et = ETIQUETA[estado];
    const abierto = st.abiertos.has(d.fecha);
    const msg = st.mensajes[d.fecha];
    const ocupado = st.ocupado === d.fecha;
    let acciones = '';
    // "Revisar despues" deja los botones: el dia sigue esperando un aceptar o un rechazar.
    if (puedeDecidir && admiteDecision(estado)) {
      acciones = st.confirmando === d.fecha
        ? `<div class="bp-acciones"><span class="bp-aviso">Se marcan ${d.paraConciliar} movimiento(s) del banco como conciliados.</span>
            <button type="button" class="btn btn-primary btn-sm" data-bp-dec="aceptar" data-bp-fecha="${esc(d.fecha)}" data-bp-confirmo="1">Si, aceptar</button>
            <button type="button" class="btn btn-ghost btn-sm" data-bp-cancelar="${esc(d.fecha)}">Cancelar</button></div>`
        : `<div class="bp-acciones">
            <button type="button" class="btn btn-primary btn-sm" data-bp-dec="aceptar" data-bp-fecha="${esc(d.fecha)}" ${ocupado ? 'disabled' : ''}>Aceptar el dia</button>
            <button type="button" class="btn btn-ghost btn-sm" data-bp-dec="rechazar" data-bp-fecha="${esc(d.fecha)}" ${ocupado ? 'disabled' : ''}>Rechazar</button>
            <button type="button" class="btn btn-ghost btn-sm" data-bp-dec="revisar" data-bp-fecha="${esc(d.fecha)}" ${ocupado ? 'disabled' : ''}>Revisar despues</button></div>`;
    }
    return `<article class="bp-dia" data-bp-dia="${esc(d.fecha)}">
      <div class="bp-cab" data-bp-abrir="${esc(d.fecha)}" role="button" aria-expanded="${abierto}">
        <span class="bp-fecha">${fechaCorta(d.fecha)}</span>
        <span style="font-size:0.875rem;color:#8b949e">${movimientosDelDia(d)} mov. · ${plata(d.suman)}</span>
        <span class="bp-estado ${et.cls}">${et.txt}</span>
      </div>
      <p class="bp-texto">${esc(d.texto)}</p>
      ${abierto ? `
        ${d.filas.length ? `<div class="bp-grupo"><h4>Cruzan con una venta (${d.filas.length})</h4>${d.filas.map((f) =>
          `<div class="bp-fila"><span>Factura ${esc(f.factura || 's/n')} · caja ${plata(f.valorCaja)}</span><span>banco ${plata(f.valorBanco)}${f.mismoDia ? '' : ' (otro dia)'}</span></div>`).join('')}</div>` : ''}
        ${d.ventasSinBanco.length ? `<div class="bp-grupo"><h4>Ventas por transferencia que no aparecen en el banco (${d.ventasSinBanco.length})</h4>${d.ventasSinBanco.map((v) =>
          `<div class="bp-fila"><span>Factura ${esc(v.factura || 's/n')}</span><span>${plata(v.valor)}</span></div>`).join('')}</div>` : ''}
        ${htmlMovs('Entradas del banco sin venta', d.bancoSinVenta)}
        ${htmlMovs('Movimientos de Nexo', d.pagosNexo)}
        ${htmlSalidas(d)}
      ` : `<button type="button" class="bp-chip" data-bp-abrir="${esc(d.fecha)}" style="margin-top:6px">Ver el detalle</button>`}
      ${acciones}
      ${msg ? `<div class="${msg.cls}" style="margin-top:6px">${esc(msg.txt)}</div>` : ''}
    </article>`;
  }

  function pintar() {
    if (!raiz) return;
    const datos = st.datos;
    const r = datos ? resumenBandeja(datos.dias, st.decisiones) : null;
    const dias = datos ? filtrarDias(datos.dias, st.filtro, st.decisiones) : [];
    const filtros = [['todos', 'Todos'], ['sin-decidir', 'Sin decidir'], ['diferencias', 'Con diferencias'], ['decididos', 'Decididos']];
    raiz.innerHTML = `
      <div class="bp-barra">
        <label>Mes<input type="month" data-bp-mes value="${esc(st.mes)}"></label>
        <button type="button" class="btn btn-primary btn-sm" data-bp-cargar ${st.cargando ? 'disabled' : ''}>${st.cargando ? 'Cargando…' : 'Ver propuestas'}</button>
      </div>
      ${puedeDecidir ? '' : '<div class="bp-aviso">Puedes ver las propuestas; solo el administrador las decide.</div>'}
      ${st.error ? `<div class="bp-error">${esc(st.error)}</div>` : ''}
      ${datos ? `
        <div class="bp-mes">${esc(datos.mes.texto || 'Sin resumen del mes.')}
          <div class="bp-cuentas">
            <span><b>${r.dias}</b> dias con movimientos</span>
            <span><b>${r.sinDecidir}</b> sin decidir</span>
            <span><b>${r.cuadran}</b> cuadran</span>
            <span><b>${r.conDiferencias}</b> con diferencias</span>
            <span><b>${r.paraRevisar}</b> para revisar</span>
            <span><b>${r.decididos}</b> decididos</span>
            <span><b>${r.salidasSinEgreso}</b> salidas sin gasto</span>
            <span><b>${r.pagosNexo}</b> de Nexo</span>
          </div>
        </div>
        <div class="bp-filtros">${filtros.map(([k, t]) => `<button type="button" class="bp-chip" data-bp-filtro="${k}" aria-pressed="${st.filtro === k}">${t}</button>`).join('')}</div>
        ${datos.dias.length === 0 ? '<p class="empty-state">No hay movimientos del banco en ese mes. Si el extracto no esta cargado, subelo en "Subir extracto".</p>'
          : dias.length === 0 ? '<p class="empty-state">Ningun dia con ese filtro.</p>'
          : dias.map(htmlDia).join('')}
      ` : ''}`;
    enlazar();
  }

  function enlazar() {
    raiz.querySelector('[data-bp-mes]')?.addEventListener('change', (e) => { st.mes = e.target.value; });
    raiz.querySelector('[data-bp-cargar]')?.addEventListener('click', cargar);
    raiz.querySelectorAll('[data-bp-filtro]').forEach((b) => b.addEventListener('click', () => { st.filtro = b.dataset.bpFiltro; pintar(); }));
    raiz.querySelectorAll('[data-bp-abrir]').forEach((b) => b.addEventListener('click', () => {
      const f = b.dataset.bpAbrir;
      if (st.abiertos.has(f)) st.abiertos.delete(f); else st.abiertos.add(f);
      pintar();
    }));
    raiz.querySelectorAll('[data-bp-dec]').forEach((b) => b.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const fecha = b.dataset.bpFecha;
      const decision = b.dataset.bpDec;
      // Aceptar marca movimientos del banco: pide un segundo clic. Rechazar y revisar solo guardan la decision.
      if (decision === DECISIONES.ACEPTAR && !b.dataset.bpConfirmo) { st.confirmando = fecha; pintar(); return; }
      decidirDia(fecha, decision);
    }));
    raiz.querySelectorAll('[data-bp-cancelar]').forEach((b) => b.addEventListener('click', () => { st.confirmando = null; pintar(); }));
    raiz.querySelectorAll('[data-bp-eg-dec]').forEach((b) => b.addEventListener('click', () => {
      const mov = b.dataset.bpMov;
      const fecha = b.closest('[data-bp-dia]')?.dataset.bpDia;
      decidirEgreso(fecha, mov, b.dataset.bpEgDec);
    }));
  }

  return {
    estado: st,
    /** Pinta la bandeja en el elemento (se puede llamar en cada render del padre: el estado se conserva). */
    montar(el) {
      if (!el) return;
      raiz = el;
      raiz.classList.add('ban-prop');
      ponerCss(el.ownerDocument);
      pintar();
    },
    cargar
  };
}

// Un id de movimiento es un uuid; por si acaso, se escapa para el selector.
function CSS_ESC(s) {
  return String(s).replace(/["\\]/g, '\\$&');
}
